import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Users, Mail, Layers, KanbanSquare, Settings as SettingsIcon,
  LogOut, ChevronRight,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/campaigns", label: "Campaigns", icon: Mail },
  { to: "/tiers", label: "Partnership Tiers", icon: Layers },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F8F7F5]">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 sidebar-dark flex flex-col" data-testid="app-sidebar">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="font-heading text-white text-xl leading-tight tracking-tight">
            Paisley
            <span className="block text-[#D4AF37] text-sm mt-0.5 font-normal">Partnership Engine</span>
          </div>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV.map(({ to, label, icon: Icon, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-[#D1D5DB] hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          {user && (
            <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
              <div className="h-8 w-8 rounded-sm bg-[#1B4332] text-white text-xs flex items-center justify-center font-medium">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{user.name}</div>
                <div className="text-[#9CA3AF] text-xs truncate">{user.email}</div>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#D1D5DB] hover:text-white hover:bg-white/5 rounded-sm transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-200 h-14 flex items-center px-8" data-testid="app-header">
          <div className="text-xs uppercase-tiny text-gray-500 flex items-center gap-1">
            <span>Paisley.org.uk</span>
            <ChevronRight className="h-3 w-3" />
            <span className="text-gray-800">Internal Outreach</span>
          </div>
        </header>
        <div className="flex-1 p-8 min-w-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
